import { API_URLS } from '../config/apiConfig';
import { makeAuthenticatedRequestSBU } from '../apiUtils';
import { UserRole } from '../roleConfig';
import { fetchAuthSession } from 'aws-amplify/auth';

interface UserRoleResponse {
  email: string;
  roles: string;
  permissions: string[];
  name?: string;
  upload_accesses: string[];
}

interface UserRoleResult {
  roles: UserRole[];
  name?: string;
  upload_accesses?: string[];
}

// New interfaces for the admin all-user roles functionality
export interface UserDetail {
  name?: string;
  email: string;
  templates: string[];
  role: 'Primary' | 'Secondary';
}

export interface EntityUserInfo {
  entityCode: string;
  primaryUser?: UserDetail;
  secondaryUser?: UserDetail;
}

export const fetchUserRole = async (email: string): Promise<UserRoleResult> => {
  try {
    const lowercaseEmail = email.toLowerCase();
    const queryParams = new URLSearchParams({
      email: lowercaseEmail,
      version: 'v2'
    });
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    const response = await makeAuthenticatedRequestSBU(
      `${API_URLS.USER_ROLE}?${queryParams}`,
      {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorStatus = response.status;
      // Explicitly handle 404 errors - user role not found
      if (errorStatus === 404) {
        throw new Error(`User role not found (404): User '${email}' may not have access to this application.`);
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: UserRoleResponse = await response.json();
    
    let roles: UserRole[] = [];
    if (data.roles) {
      const roleArray = data.roles.includes(',') 
        ? data.roles.split(',').map(role => role.trim())
        : [data.roles.trim()];
      
      // Extract only the part before the '|' if present
      const cleanedRoles = roleArray.map(role => role.split('|')[0].trim());
      
      const validRoles = cleanedRoles.filter(role => Object.values(UserRole).includes(role as UserRole));
      if (validRoles.length > 0) {
        roles = validRoles.map(role => role as UserRole);
      }
    }
    
    return {
      roles,
      name: data.name,
      upload_accesses: data.upload_accesses
    };
  } catch (error) {
    console.error('Error fetching user role:', error);
    // Instead of returning a default value, throw the error so it can be properly handled upstream
    throw error;
  }
};

// Function to check if a user has admin permissions
export const hasAdminPermission = (userRoles: UserRole[]): boolean => {
  return userRoles.some(role => ADMIN_ROLES.includes(role));
};


export const ADMIN_ROLES: UserRole[] = [
  UserRole.Admin,
  UserRole.TrialActualAdmin,
  UserRole.TrialBudgetAdmin,
  UserRole.BusinessActualAdmin,
  UserRole.BusinessBudgetAdmin,
  UserRole.ActualViewAdmin,
  UserRole.BudgetViewAdmin,
];


// Function to convert the entity map to a flat array for easier joining in dashboard
export const entityMapToFlatArray = (entityMap: Map<string, EntityUserInfo>): Array<{
  entityCode: string;
  template: string;
  primaryUserEmail?: string;
  primaryUserName?: string;
  secondaryUserEmail?: string;
  secondaryUserName?: string;
  combinedUsers?:string;
}> => {
  const flatArray: Array<{
    entityCode: string;
    template: string;
    primaryUserEmail?: string;
    primaryUserName?: string;
    secondaryUserEmail?: string;
    secondaryUserName?: string;
    combinedUsers?:string;
  }> = [];
  
  // Iterate through each entity
  entityMap.forEach((entityInfo, entityCode) => {
    const { primaryUser, secondaryUser } = entityInfo;
    
    // Get unique templates from both primary and secondary users
    const primaryTemplates = primaryUser?.templates || [];
    const secondaryTemplates = secondaryUser?.templates || [];
    const allTemplates = [...new Set([...primaryTemplates, ...secondaryTemplates])];
    
    // For each template, create a row
    allTemplates.forEach(template => {
      flatArray.push({
        entityCode,
        template,
        primaryUserEmail: primaryUser?.email,
        primaryUserName: primaryUser?.name,
        secondaryUserEmail: secondaryUser?.email,
        secondaryUserName: secondaryUser?.name,
        combinedUsers: (() => {
                        const p = (primaryUser?.name ?? '').trim();
                        const s = (secondaryUser?.name ?? '').trim();
                        if (p && s) return `${p} | ${s}`;
                        return p || s || '-';
                       })()
      });
    });
  });
  
  return flatArray;
};

// Update the return type of fetchAllUserRoles to include the flat array
export const fetchAllUserRoles = async (email: string): Promise<{
  entityUserMap: Map<string, EntityUserInfo>;
  flatEntityData: ReturnType<typeof entityMapToFlatArray>;
  userEmail?: string;
  userName?: string;
}> => {
  try {
    const queryParams = new URLSearchParams({
      version: 'Full',
      email: email.toLowerCase()
    });
    
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    const response = await makeAuthenticatedRequestSBU(
      `${API_URLS.USER_ROLE}?${queryParams}`,
      {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorStatus = response.status;
      // Explicitly handle 404 errors
      if (errorStatus === 404) {
        throw new Error(`Admin user role data not found (404): Unable to retrieve role data for user '${email}'.`);
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // The API returns an object with items array containing user objects
    const responseData = await response.json();
    
    // Log the raw response data
    console.log('Raw API response for all user roles:', responseData);
    
    // Extract the items array containing user data
    const usersData = responseData.items || [];
    
    if (!Array.isArray(usersData) || usersData.length === 0) {
      console.warn('No user data found in API response or invalid format');
      throw new Error('No user data found in API response or invalid format');
    }
    
    console.log(`Processing ${usersData.length} users from API response`);
    
    // Process the array of users and build the entity map
    const entityUserMap = processUsersData(usersData);
    
    // Convert to flat array for easier joining
    const flatEntityData = entityMapToFlatArray(entityUserMap);
    
    // Log the processed data
    console.log('Processed entity user map:', {
      entityCount: entityUserMap.size,
      entityCodes: Array.from(entityUserMap.keys()),
      sampleEntity: entityUserMap.size > 0 ? 
        Array.from(entityUserMap.entries())[0] : 
        null
    });
    
    // Use the data from the current user
    const currentUserData = usersData.find((userData: any) => 
      userData.email?.toLowerCase() === email.toLowerCase()
    );
    
    return {
      entityUserMap,
      flatEntityData,
      userEmail: currentUserData?.email,
      userName: currentUserData?.name
    };
  } catch (error) {
    console.error('Error fetching all user roles:', error);
    // Instead of returning an empty result, throw the error so it can be handled upstream
    throw error;
  }
};

// Function to process the array of users data
const processUsersData = (usersData: any[]): Map<string, EntityUserInfo> => {
  const entityUserMap = new Map<string, EntityUserInfo>();
  
  // Process each user
  for (const userData of usersData) {
    try {
      const { email, name, upload_accesses } = userData;
      
      // Skip users with no upload accesses or invalid data
      if (!upload_accesses || upload_accesses === "code:000-:nan|files:[]") {
        continue;
      }
      
      console.log(`Processing user ${name} (${email}) with upload_accesses:`, upload_accesses);
      
      // Parse the upload_accesses string to extract entity codes and role types
      const entitySections = upload_accesses.split('||').filter(Boolean);
      
      for (const section of entitySections) {
        try {
          // Parse each section which follows the format:
          // code:0024:Primary|files:[Trial Balance Plus - Actual;...]
          const codeMatch = section.match(/code:([^:]+):(Primary|Secondary|nan)/i);
          
          if (codeMatch) {
            const entityCode = codeMatch[1].replace('-', ''); // Remove any dash
            const roleType = (codeMatch[2] === 'nan' ? 'Primary' : codeMatch[2]) as 'Primary' | 'Secondary';
            
            // Skip entity code 000
            if (entityCode === '000') {
              continue;
            }
            
            // Extract templates
            const templatesMatch = section.match(/files:\[(.*?)\]/);
            const templates = templatesMatch 
              ? templatesMatch[1].split(';').map((t: string) => t.trim()).filter(Boolean)
              : [];
            
            console.log(`Found entity ${entityCode} with role ${roleType} for user ${email}, templates: ${templates.length}`);
            
            // Get or create entity info
            let entityInfo = entityUserMap.get(entityCode);
            if (!entityInfo) {
              entityInfo = {
                entityCode
              };
              entityUserMap.set(entityCode, entityInfo);
            }
            
            // Create the user detail object
            const userDetail: UserDetail = {
              email,
              name: name && name !== 'nan' ? name : undefined,
              templates,
              role: roleType
            };
            
            // Assign the user to the appropriate role in the entity
            if (roleType === 'Primary') {
              entityInfo.primaryUser = userDetail;
            } else {
              entityInfo.secondaryUser = userDetail;
            }
          } else {
            console.warn('Failed to match code pattern in section:', section);
          }
        } catch (error) {
          console.error('Error processing section for user:', email, section, error);
        }
      }
    } catch (error) {
      console.error('Error processing user data:', userData, error);
    }
  }
  
  return entityUserMap;
};

// Function to populate user email and name information in the entity map
export const populateUserInfo = (
  entityUserMap: Map<string, EntityUserInfo>,
  userInfo: Record<string, { email: string; name?: string }>
): Map<string, EntityUserInfo> => {
  // Make a copy of the map to avoid modifying the original
  const updatedMap = new Map(entityUserMap);
  
  // For each entity in the map
  updatedMap.forEach((entityInfo, entityCode) => {
    // If there's a primary user and we have info for it
    if (entityInfo.primaryUser) {
      const primaryUser = entityInfo.primaryUser;
      const roleIdentifier = `${entityCode}:Primary`;
      
      if (userInfo[roleIdentifier]) {
        primaryUser.email = userInfo[roleIdentifier].email;
        primaryUser.name = userInfo[roleIdentifier].name;
      }
    }
    
    // If there's a secondary user and we have info for it
    if (entityInfo.secondaryUser) {
      const secondaryUser = entityInfo.secondaryUser;
      const roleIdentifier = `${entityCode}:Secondary`;
      
      if (userInfo[roleIdentifier]) {
        secondaryUser.email = userInfo[roleIdentifier].email;
        secondaryUser.name = userInfo[roleIdentifier].name;
      }
    }
  });
  
  return updatedMap;
};

// Function that first fetches user role and then fetches all roles if admin
export const fetchUserRoleWithAdminCheck = async (email: string): Promise<{
  userResult: UserRoleResult;
  adminData?: {
    entityUserMap: Map<string, EntityUserInfo>;
    flatEntityData: ReturnType<typeof entityMapToFlatArray>;
    userEmail?: string;
    userName?: string;
  }
}> => {
  try {
    // First fetch the user's regular role
    const userResult = await fetchUserRole(email);
    console.log('User role result:', userResult);
    
    // Check if user has admin privileges
    if (hasAdminPermission(userResult.roles)) {
      console.log('User has admin permissions, fetching all user roles...');
      // If they're an admin, fetch all user roles
      const adminData = await fetchAllUserRoles(email);
      return { userResult, adminData };
    }
    
    // If not an admin, just return the user result
    return { userResult };
  } catch (error) {
    console.error('Error in fetchUserRoleWithAdminCheck:', error);
    // Re-throw the error instead of returning a default value
    // This allows the error to be properly handled in userContext.tsx
    throw error;
  }
};

// Helper function to join the flat entity data with existing table data
export const joinTableDataWithEntityData = (
  tableData: any[], 
  flatEntityData: ReturnType<typeof entityMapToFlatArray>
): any[] => {
  // Create a map for quick lookups
  const entityTemplateMap = new Map<string, any>();
  
  // Create a key using entityCode and template
  flatEntityData.forEach(item => {
    const key = `${item.entityCode}:${item.template}`;
    entityTemplateMap.set(key, item);
  });
  
  // Join the data
  return tableData.map(tableItem => {
    // Extract entity code - handle both formats
    const entityCode = tableItem.entityCode || tableItem.entityCodeAbbr;
    if (!entityCode) return tableItem;
    
    // Extract template name
    const templateName = tableItem.fileName;
    if (!templateName) return tableItem;
    
    // Look up the entity data
    const key = `${entityCode}:${templateName}`;
    const entityData = entityTemplateMap.get(key);
    
    if (!entityData) return tableItem;
    
    // Return merged data
    return {
      ...tableItem,
      primaryUserEmail: entityData.primaryUserEmail,
      primaryUserName: entityData.primaryUserName,
      secondaryUserEmail: entityData.secondaryUserEmail,
      secondaryUserName: entityData.secondaryUserName,
      combinedUsers: entityData.combinedUsers
    };
  });
};
